/**
 * Scroll-pinned vertical image stack: page scroll drives the track until fully
 * revealed, then normal scrolling continues.
 */
(function () {
  class VvScrollStack {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.driveClass = root.dataset.scrollDriveClass || '';
      const minWidth = root.dataset.scrollMinWidth || '990';
      this.desktopMq = window.matchMedia(`(min-width: ${minWidth}px)`);
      this.scroller = root.querySelector('[data-scroll-scroller]');
      this.pin = root.querySelector('[data-scroll-pin]');
      this.windowEl = root.querySelector('[data-scroll-window]');
      this.track = root.querySelector('[data-scroll-track]');
      this.reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.maxTranslate = 0;
      this.enabled = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMqChange = this.onMqChange.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onGalleryChange = this.onGalleryChange.bind(this);

      this.desktopMq.addEventListener('change', this.onMqChange);
      this.reducedMotionMq.addEventListener('change', this.onReducedMotionChange);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.root.addEventListener('vv:gallery-change', this.onGalleryChange);

      if (this.track) {
        this.track.querySelectorAll('img').forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', this.onResize, { once: true });
          }
        });
      }

      this.setup();
    }

    onMqChange() {
      this.setup();
    }

    onReducedMotionChange() {
      this.setup();
    }

    onGalleryChange() {
      if (!this.enabled) return;
      if (this.track) this.track.style.transform = 'translateY(0)';
      this.measure();
      this.onScroll();
    }

    setup() {
      const shouldEnable =
        this.desktopMq.matches &&
        !this.reducedMotionMq.matches &&
        Boolean(this.scroller && this.pin && this.windowEl && this.track);

      if (shouldEnable && !this.enabled) {
        this.enabled = true;
        if (this.driveClass) this.root.classList.add(this.driveClass);
        this.measure();
        this.onScroll();
        return;
      }

      if (!shouldEnable && this.enabled) {
        this.teardown();
      } else if (shouldEnable) {
        this.measure();
        this.onScroll();
      }
    }

    teardown() {
      this.enabled = false;
      if (this.driveClass) this.root.classList.remove(this.driveClass);
      if (this.scroller) this.scroller.style.height = '';
      if (this.track) this.track.style.transform = '';
    }

    measure() {
      if (!this.enabled || !this.scroller || !this.windowEl || !this.track) return;

      this.maxTranslate = Math.max(0, this.track.scrollHeight - this.windowEl.clientHeight);
      this.scroller.style.height = `${window.innerHeight + this.maxTranslate}px`;
    }

    onResize() {
      if (!this.enabled) return;
      this.measure();
      this.onScroll();
    }

    onScroll() {
      if (!this.enabled || !this.scroller || !this.track) return;

      const rect = this.scroller.getBoundingClientRect();
      const scrollable = this.scroller.offsetHeight - window.innerHeight;

      if (scrollable <= 0) {
        this.track.style.transform = 'translateY(0)';
        return;
      }

      const progress = Math.min(1, Math.max(0, -rect.top / scrollable));
      this.track.style.transform = `translate3d(0, ${-progress * this.maxTranslate}px, 0)`;
    }
  }

  function init() {
    document.querySelectorAll('[data-vv-scroll-stack]').forEach((root) => {
      if (root instanceof HTMLElement && !root.dataset.vvScrollStackInit) {
        root.dataset.vvScrollStackInit = 'true';
        new VvScrollStack(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
